/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as IconDelete } from "@shared/assets/Icons/IconDelete_KE.svg";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { ReactComponent as IconTag } from "@shared/assets/Icons/IconTag.svg";
import GenericCard from "@ui-components/Card/GenericCard";

/**
 * Legacy free-text tag editor for resource detail cards.
 *
 * Kept verbatim from the pre-Tags-Manager era so production users keep
 * their familiar editor while the new global Tags Manager (rewritten in
 * `TagManagementModal.tsx`) rolls out behind `isProductionEnvironment()`.
 *
 * Each tag is a plain string; the parent persists them as a comma-
 * separated value on the resource record via `updateConnectionMeta`.
 *
 * @see `EditableTagList.tsx` — picks legacy vs picker based on env.
 */

interface LegacyTagManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTags: string[];
  onSave: (tags: string[]) => Promise<void>;
  maxTags?: number;
}

const MIN_TAG_LENGTH = 2;
const MAX_TAG_LENGTH = 50;
const DEFAULT_MAX_TAGS = Infinity;

export const LegacyTagManagementModal: React.FC<
  LegacyTagManagementModalProps
> = ({ isOpen, onClose, initialTags, onSave, maxTags = DEFAULT_MAX_TAGS }) => {
  const [localTags, setLocalTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState<string>("");
  const [newlyAddedIndices, setNewlyAddedIndices] = useState<Set<number>>(
    new Set()
  );
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Record<number, string>>(
    {}
  );

  const newTagInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalTags([...initialTags]);
      setNewlyAddedIndices(new Set());
      setNewTagInput("");
      setEditingValues({});
      setValidationErrors([]);
      setApiError(null);
    }
  }, [isOpen, initialTags]);

  useEffect(() => {
    if (isOpen && newTagInputRef.current) {
      const input = newTagInputRef.current.querySelector("input");
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
    }
  }, [isOpen]);

  const validateTag = (
    tag: string,
    existingTags: string[],
    excludeIndex?: number
  ): string | null => {
    const trimmed = tag.trim();

    if (trimmed.length < MIN_TAG_LENGTH) {
      return `Tag must have at least ${MIN_TAG_LENGTH} characters`;
    }

    if (trimmed.length > MAX_TAG_LENGTH) {
      return `Tag must not exceed ${MAX_TAG_LENGTH} characters`;
    }

    const otherTags = existingTags.filter((_, idx) => idx !== excludeIndex);
    if (otherTags.includes(trimmed)) {
      return "This tag already exists";
    }

    return null;
  };

  const handleAddTag = () => {
    if (localTags.length >= maxTags) {
      setValidationErrors([
        `Maximum ${maxTags} tags allowed. Delete a tag to add more.`,
      ]);
      return;
    }

    const error = validateTag(newTagInput, localTags);
    if (error) {
      setValidationErrors([error]);
      return;
    }

    const trimmedTag = newTagInput.trim();
    const newIndex = 0;
    setLocalTags([trimmedTag, ...localTags]);
    setNewlyAddedIndices(
      new Set([newIndex, ...Array.from(newlyAddedIndices).map((i) => i + 1)])
    );
    setNewTagInput("");
    setValidationErrors([]);

    setTimeout(() => {
      if (newTagInputRef.current) {
        const input = newTagInputRef.current.querySelector("input");
        if (input) {
          input.focus();
        }
      }
    }, 50);
  };

  const handleEditTag = (index: number, value: string) => {
    setEditingValues({ ...editingValues, [index]: value });
  };

  const handleDeleteTag = (index: number) => {
    const updatedTags = localTags.filter((_, i) => i !== index);
    setLocalTags(updatedTags);

    const updatedNewlyAdded = new Set<number>();
    newlyAddedIndices.forEach((idx) => {
      if (idx < index) {
        updatedNewlyAdded.add(idx);
      } else if (idx > index) {
        updatedNewlyAdded.add(idx - 1);
      }
    });
    setNewlyAddedIndices(updatedNewlyAdded);

    const newEditingValues = { ...editingValues };
    delete newEditingValues[index];
    const shiftedEditingValues: Record<number, string> = {};
    Object.entries(newEditingValues).forEach(([key, val]) => {
      const keyNum = Number(key);
      if (keyNum < index) {
        shiftedEditingValues[keyNum] = val;
      } else if (keyNum > index) {
        shiftedEditingValues[keyNum - 1] = val;
      }
    });
    setEditingValues(shiftedEditingValues);
  };

  const handleSave = async () => {
    const errors: string[] = [];
    const finalTags: string[] = [];

    localTags.forEach((tag, index) => {
      const editedValue = editingValues[index];
      const finalValue =
        editedValue !== undefined ? editedValue.trim() : tag.trim();

      if (finalValue === "") {
        errors.push(`Tag at position ${index + 1} cannot be empty`);
        return;
      }

      const error = validateTag(finalValue, finalTags, index);
      if (error) {
        errors.push(`Tag at position ${index + 1}: ${error}`);
        return;
      }

      finalTags.push(finalValue);
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSaving(true);
    setValidationErrors([]);
    setApiError(null);

    try {
      await onSave(finalTags);
      onClose();
    } catch (error: any) {
      console.error("Error saving tags:", error);
      setApiError(
        error?.message || "Failed to save tags. Please try again later."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalTags([...initialTags]);
    setNewlyAddedIndices(new Set());
    setNewTagInput("");
    setEditingValues({});
    setValidationErrors([]);
    setApiError(null);
    onClose();
  };

  const handleNewTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleTagKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const canAddMore = localTags.length < maxTags;
  const isAtMax = localTags.length >= maxTags;

  const modalTitle = (
    <div className="flex items-center gap-2 pb-3 border-b border-semantic-border-secondary">
      <div className="flex p-1 border border-semantic-border-secondary bg-semantic-bg-secondary rounded items-center justify-center">
        <IconTag className="w-5 h-5 text-semantic-text-primary" />
      </div>
      <span className="font-semibold text-semantic-text-primary">
        Edit tags
      </span>
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={modalTitle}
      size="lg"
      closeOnBackdropClick={!isSaving}
    >
      <div className="px-2 sm:px-5">
        <div className="flex flex-col gap-4 h-full">
          {apiError && (
            <GenericAlert
              isOpen={true}
              onClose={() => setApiError(null)}
              onConfirm={() => setApiError(null)}
              title="Error Saving Tags"
              description={apiError}
              variant="error"
              confirmLabel="OK"
            />
          )}

          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 flex-shrink-0">
              <p className="text-sm font-medium text-red-800 mb-2">
                Validation Errors:
              </p>
              <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col-reverse items-end gap-3 sm:flex-row sm:items-end sm:justify-start">
            <div className="flex w-full flex-col gap-2 flex-shrink-0 sm:w-auto">
              <div ref={newTagInputRef} className="flex-1">
                <GenericInput
                  value={newTagInput}
                  size="small"
                  label="Add tag"
                  onChange={(val) => {
                    const truncated =
                      val.length > MAX_TAG_LENGTH
                        ? val.slice(0, MAX_TAG_LENGTH)
                        : val;
                    setNewTagInput(truncated);
                    setValidationErrors([]);
                  }}
                  onKeyDown={handleNewTagKeyDown}
                  disabled={!canAddMore || isSaving}
                  placeholder={isAtMax ? `Maximum ${maxTags} tags reached` : ""}
                  fullWidth
                />
              </div>
            </div>
            <GenericButton
              label="+ Add tag"
              variant="secondary"
              size="sm"
              onClick={handleAddTag}
              disabled={!canAddMore || isSaving || !newTagInput.trim()}
              iconPosition="left"
            />
          </div>

          <GenericCard isCollapsible={false}>
            {localTags.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-sm text-semantic-text-secondary">
                  No tags yet. Add your first tag above.
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {localTags.map((tag, index) => {
                  const editedValue = editingValues[index];
                  const displayValue =
                    editedValue !== undefined ? editedValue : tag;
                  const isNewlyAdded = newlyAddedIndices.has(index);

                  return (
                    <div
                      key={index}
                      className="flex flex-wrap items-center gap-x-2 gap-y-1 p-2 border-b border-semantic-border-secondary last:border-b-0 sm:gap-x-3 sm:p-3"
                    >
                      <div className="order-1 flex-shrink-0">
                        <IconTag className="w-4 h-4 text-semantic-text-secondary" />
                      </div>

                      <div className="order-2 flex-1 min-w-0 [&_input]:!w-full sm:[&_input]:!w-[240px]">
                        <GenericInput
                          value={displayValue}
                          onChange={(val) => {
                            const truncated =
                              val.length > MAX_TAG_LENGTH
                                ? val.slice(0, MAX_TAG_LENGTH)
                                : val;
                            handleEditTag(index, truncated);
                          }}
                          onKeyDown={(e) => handleTagKeyDown(index, e)}
                          disabled={isSaving}
                          placeholder="Tag name"
                          fullWidth={false}
                          size="small"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDeleteTag(index)}
                        disabled={isSaving}
                        className="order-3 inline-flex items-center justify-center w-8 h-8 rounded bg-semantic-bg-danger-secondary hover:bg-gray-100 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 sm:order-4"
                        title="Delete tag"
                      >
                        <IconDelete className="w-4 h-4 text-semantic-fg-danger" />
                      </button>

                      {isNewlyAdded && (
                        <span className="order-4 w-full text-xs text-semantic-text-brand font-medium sm:order-3 sm:w-auto sm:whitespace-nowrap">
                          Newly added
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </GenericCard>

          <div className="flex justify-end gap-3 pt-2 border-t border-semantic-border-secondary flex-shrink-0 mt-5">
            <GenericButton
              label="Cancel"
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
            />
            <GenericButton
              label={isSaving ? "Saving..." : "Save"}
              variant="brand"
              onClick={handleSave}
              disabled={isSaving}
              loading={isSaving}
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};
